import { Button, Stack, Tooltip } from '@mui/material';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextFieldControl } from '../../../component/form';
import { CarFormControls, CarFormValues, useCarForm } from './useCarForm';
import { useCarPrecondition } from './useCarPrecondition';

interface CarConnectFormProps {
  onSubmit?: () => void;
}

const CarConnectForm: React.FC<CarConnectFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation('benefitsModule');
  const { isPreconditionMet } = useCarPrecondition();
  const { form, createCarCompanyCode, isLoading, disabled: formDisabled, handleReset } = useCarForm({ onConnected: onSubmit });
  const resetDisabled = !isPreconditionMet || !form.formState.isDirty || isLoading;
  const handleSave = useCallback(
    (values: CarFormValues) => {
      createCarCompanyCode.mutate(values);
    },
    [createCarCompanyCode],
  );

  return (
    <form onSubmit={form.handleSubmit(handleSave)} style={{ flexGrow: 1 }}>
      <TextFieldControl
        control={form.control}
        name={CarFormControls.companyCode}
        sx={{ width: 500, mb: 2 }}
        slotProps={{
          htmlInput: {
            sx: { textAlign: 'center' },
          },
        }}
        label={t('CAR.connect.form.name')}
        rules={{
          pattern: /^[0-9]*$/,
          maxLength: 12,
          validate: v => (!v ? t('CAR.connect.form.error') : /^\d{10,12}$/.test(v) || t('CAR.connect.form.error')),
        }}
        helperText={t('CAR.connect.form.hint')}
      />
      <Stack spacing={2} direction="row" justifyContent="center">
        {isPreconditionMet && (
          <Button variant="text" size="small" type="reset" disabled={resetDisabled} onClick={handleReset}>
            {t('common:buttons.discard')}
          </Button>
        )}

        <Tooltip arrow placement="top" title={t('common:buttons.saveDisabledTooltip')} disableHoverListener={!formDisabled}>
          <span>
            <LoadingButton size="small" type="submit" disabled={formDisabled} loading={isLoading} data-test-id="settings-form-panel-save">
              {t('common:buttons.save')}
            </LoadingButton>
          </span>
        </Tooltip>
      </Stack>
    </form>
  );
};

export default CarConnectForm;
